import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { 
  Typography, Container, Paper, Button, 
  List, ListItem, ListItemText, CircularProgress, Snackbar,
  Grid, ThemeProvider, createTheme, CssBaseline,
  Box, Alert, TextField, Select, MenuItem, InputAdornment,
  IconButton, Stepper, Step, StepLabel, Card, CardContent,
  Chip, Divider, Tooltip, Zoom, Collapse, Slider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import KitchenIcon from '@mui/icons-material/Kitchen';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import PrintIcon from '@mui/icons-material/Print';

const genAI = new GoogleGenerativeAI('AIzaSyD9aN0yTsRxuXvZwl5kQA8THMc7ZJQIzd0');
const theme = createTheme({
  palette: {
    primary: {
      main: '#5D9C59',
    },
    secondary: {
      main: '#DF7861',
    },
    background: {
      default: '#F8EDE3',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#3D3C42',
    },
  },
  typography: {
    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '10px 20px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          overflow: 'visible',
        },
      },
    },
  },
});

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[8],
  },
}));

const RecipeIconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: 140,
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.contrastText,
}));

function App() {
  const [images, setImages] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newIngredient, setNewIngredient] = useState('');
  const [cuisine, setCuisine] = useState('Chinese');
  const [diet, setDiet] = useState('No Preference');
  const [showCamera, setShowCamera] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [recipeCount, setRecipeCount] = useState(4);
  const [expandedRecipes, setExpandedRecipes] = useState({});
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const steps = ['Upload Photos', 'Identify Ingredients', 'Generate Recipes'];

  useEffect(() => {
    if (images.length > 0) setActiveStep(1);
    if (ingredients.length > 0) setActiveStep(2);
    if (recipes.length > 0) setActiveStep(3);
  }, [images, ingredients, recipes]);

  useEffect(() => {
    if (showCamera) {
      startCamera();
    } else {
      stopCamera();
    }
  }, [showCamera]);

  const handleImageUpload = useCallback((event) => {
    const newImages = Array.from(event.target.files);
    setImages((prevImages) => [...prevImages, ...newImages]);
  }, []);

  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
      setShowCamera(true);
    } catch (err) {
      console.error("Error accessing the camera:", err);
      let errorMessage = "Unable to access the camera. ";
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage += "Please grant camera permissions in your browser settings.";
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage += "No camera found on your device.";
      } else if (err.name === 'NotSupportedError') {
        errorMessage += "Your browser doesn't support camera access.";
      } else if (err.name === 'InsecureContextError') {
        errorMessage += "Camera access requires a secure connection (HTTPS).";
      } else {
        errorMessage += "Please check your camera and try again.";
      }
      setCameraError(errorMessage);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setShowCamera(false);
  };

  const capturePhoto = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        const newImage = new File([blob], "camera_capture.jpg", { type: "image/jpeg" });
        setImages(prev => [...prev, newImage]);
      }, 'image/jpeg');
      
      setShowCamera(false);
      stopCamera();
    }
  }, []);

  const imageToBase64 = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = (error) => reject(error);
    });
  }, []);

  const parseIngredientsText = useCallback((text) => {
    const cleanedText = text.replace(/\r/g, '');
    const rawItems = cleanedText
      .split(/[\n,]/)
      .map((item) => item.trim())
      .filter(Boolean);

    const filtered = [];
    const seen = new Set();

    rawItems.forEach((item) => {
      let sanitized = item
        .replace(/^[•*\-\d\.\)\(]+\s*/g, '')
        .replace(/\.$/, '')
        .trim();

      if (sanitized.includes(':')) {
        sanitized = sanitized.split(':').pop().trim();
      }

      if (
        !sanitized ||
        /^(based on|here (?:is|are)|this image|the image|food items|items you can see|ingredients include|from the image)/i.test(sanitized)
      ) {
        return;
      }

      const normalized = sanitized.toLowerCase();
      if (!seen.has(normalized)) {
        seen.add(normalized);
        filtered.push(sanitized);
      }
    });

    return filtered;
  }, []);

  const identifyIngredients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const model = genAI.getGenerativeModel({ model: 'gemini-2.5-pro' });

      const allIngredients = [];
      for (const image of images) {
        const base64Image = await imageToBase64(image);
        const result = await model.generateContent([
          'List all the food items you can see in this fridge image. Provide the list in a comma-separated format.',
          { inlineData: { data: base64Image, mimeType: 'image/jpeg' } },
        ]);
        const response = await result.response;
        const text = response.text();
        const items = parseIngredientsText(text);
        allIngredients.push(...items);
      }

      const uniqueIngredientsMap = new Map(
        ingredients.map((item) => [item.toLowerCase(), item])
      );
      allIngredients.forEach((item) => {
        const normalized = item.toLowerCase();
        if (!uniqueIngredientsMap.has(normalized)) {
          uniqueIngredientsMap.set(normalized, item);
        }
      });

      setIngredients(Array.from(uniqueIngredientsMap.values()));
    } catch (err) {
      console.error('Error in identifyIngredients:', err);
      setError('Failed to identify ingredients. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [images, imageToBase64, parseIngredientsText, ingredients]);

  const addIngredient = useCallback(() => {
    const trimmedIngredient = newIngredient.trim();
    if (!trimmedIngredient) return;

    const exists = ingredients.some(
      (ingredient) => ingredient.toLowerCase() === trimmedIngredient.toLowerCase()
    );

    if (!exists) {
      setIngredients((prev) => [...prev, trimmedIngredient]);
    }

    setNewIngredient('');
  }, [ingredients, newIngredient]);

  const deleteIngredient = useCallback((index) => {
    setIngredients(prev => prev.filter((_, i) => i !== index));
  }, []);

  const generateRecipes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const model = genAI.getGenerativeModel({ model: 'gemini-2.5-pro' });
      
      const prompt = `Create ${recipeCount} recipes using these ingredients: ${ingredients.join(', ')}. 
                      Cuisine: ${cuisine}. Diet: ${diet}.
                      Provide the output as a JSON array of recipe objects. Each recipe should have a name, ingredients list, instructions list, and a short description.`;
      
      const result = await model.generateContent(prompt);
      const response = await result.response;
      const text = response.text();
      
      // Extract JSON from the response
      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        const recipesJson = JSON.parse(jsonMatch[0]);
        setRecipes(recipesJson);
        setExpandedRecipes(recipesJson.reduce((acc, _, index) => ({...acc, [index]: false}), {}));
      } else {
        throw new Error('Failed to parse recipes from the response');
      }
    } catch (err) {
      console.error('Error in generateRecipes:', err);
      setError('Failed to generate recipes. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [ingredients, cuisine, diet, recipeCount]);

  const toggleRecipeExpansion = useCallback((index) => {
    setExpandedRecipes(prev => ({...prev, [index]: !prev[index]}));
  }, []);

  const printRecipe = useCallback((recipe) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${recipe.name}</title>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; }
            h1 { color: #5D9C59; }
            h2 { color: #3D3C42; }
            ul, ol { margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <h1>${recipe.name}</h1>
          <p>${recipe.description}</p>
          <h2>Ingredients:</h2>
          <ul>
            ${recipe.ingredients.map(ing => `<li>${ing}</li>`).join('')}
          </ul>
          <h2>Instructions:</h2>
          <ol>
            ${recipe.instructions.map(step => `<li>${step}</li>`).join('')}
          </ol>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  }, []);

  const printAllRecipes = useCallback(() => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>All Recipes</title>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; }
            h1 { color: #5D9C59; }
            h2 { color: #3D3C42; }
            ul, ol { margin-bottom: 20px; }
            .recipe { margin-bottom: 40px; border-bottom: 1px solid #ccc; padding-bottom: 20px; }
          </style>
        </head>
        <body>
          <h1>All Generated Recipes</h1>
          ${recipes.map((recipe, index) => `
            <div class="recipe">
              <h2>${index + 1}. ${recipe.name}</h2>
              <p>${recipe.description}</p>
              <h3>Ingredients:</h3>
              <ul>
                ${recipe.ingredients.map(ing => `<li>${ing}</li>`).join('')}
              </ul>
              <h3>Instructions:</h3>
              <ol>
                ${recipe.instructions.map(step => `<li>${step}</li>`).join('')}
              </ol>
            </div>
          `).join('')}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  }, [recipes]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" color="primary">
          Chef's Fridge Recipe Generator
        </Typography>
        
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>1. Upload or Capture Photo</Typography>
          <Typography variant="body2" paragraph>
            Start by uploading a photo of your fridge contents or use your camera to capture one.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              component="label"
              variant="contained"
              startIcon={<CloudUploadIcon />}
            >
              Upload Photo
              <VisuallyHiddenInput 
                type="file" 
                multiple 
                onChange={handleImageUpload} 
                accept="image/*" 
              />
            </Button>
            <Button
              variant="contained"
              startIcon={<CameraAltIcon />}
              onClick={() => setShowCamera(!showCamera)}
            >
              {showCamera ? 'Close Camera' : 'Open Camera'}
            </Button>
          </Box>
          {showCamera && (
            <Box sx={{ position: 'relative', width: '100%', height: 0, paddingBottom: '75%', mb: 2 }}>
              <video 
                ref={videoRef} 
                autoPlay 
                playsInline
                style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover' }} 
              />
              <Button
                variant="contained"
                color="secondary"
                onClick={capturePhoto}
                sx={{ position: 'absolute', bottom: '16px', left: '50%', transform: 'translateX(-50%)' }}
              >
                Capture Photo
              </Button>
            </Box>
          )}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          {cameraError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {cameraError}
            </Alert>
          )}
          {images.length > 0 && (
            <Box>
              <Typography variant="body2">{images.length} image(s) uploaded</Typography>
              <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                Now, click the button below to identify ingredients from your photo(s).
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={identifyIngredients}
                disabled={loading}
                sx={{ mt: 1 }}
                startIcon={<KitchenIcon />}
              >
                Identify Ingredients
              </Button>
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>2. Ingredients</Typography>
          <Typography variant="body2" paragraph>
            Review the identified ingredients. You can add or remove ingredients as needed.
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Add new ingredient"
            value={newIngredient}
            onChange={(e) => setNewIngredient(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addIngredient();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Add Ingredient" arrow>
                    <IconButton onClick={addIngredient} disabled={!newIngredient}>
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {ingredients.map((ingredient, index) => (
              <Chip
                key={index}
                label={ingredient}
                onDelete={() => deleteIngredient(index)}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>3. Generate Recipes</Typography>
          <Typography variant="body2" paragraph>
            Customize your recipe preferences and generate delicious recipes based on your ingredients.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" gutterBottom>Cuisine Type:</Typography>
              <Select
                value={cuisine}
                onChange={(e) => setCuisine(e.target.value)}
                fullWidth
                variant="outlined"
              >
                <MenuItem value="Italian">Italian</MenuItem>
                <MenuItem value="Chinese">Chinese</MenuItem>
                <MenuItem value="Mexican">Mexican</MenuItem>
                <MenuItem value="Indian">Indian</MenuItem>
                <MenuItem value="Any">Any Cuisine</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" gutterBottom>Dietary Preference:</Typography>
              <Select
                value={diet}
                onChange={(e) => setDiet(e.target.value)}
                fullWidth
                variant="outlined"
              >
                <MenuItem value="No Preference">No Preference</MenuItem>
                <MenuItem value="Vegetarian">Vegetarian</MenuItem>
                <MenuItem value="Vegan">Vegan</MenuItem>
                <MenuItem value="Gluten-Free">Gluten-Free</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" gutterBottom>Number of Recipes:</Typography>
              <Slider
                value={recipeCount}
                onChange={(_, newValue) => setRecipeCount(newValue)}
                min={1}
                max={10}
                step={1}
                marks
                valueLabelDisplay="auto"
              />
            </Grid>
          </Grid>
          <Button 
            onClick={generateRecipes} 
            disabled={ingredients.length === 0 || loading}
            variant="contained" 
            color="primary"
            fullWidth
            startIcon={<RestaurantIcon />}
          >
            Generate Recipes
          </Button>
        </Paper>

        {recipes.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" color="primary">
                Generated Recipes
              </Typography>
              <Button
                onClick={printAllRecipes}
                startIcon={<PrintIcon />}
                variant="contained"
                color="secondary"
              >
                Print All Recipes
              </Button>
            </Box>
            <Grid container spacing={3}>
              {recipes.map((recipe, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <StyledCard>
                    <RecipeIconContainer>
                      <RestaurantMenuIcon style={{ fontSize: 60 }} />
                    </RecipeIconContainer>
                    <CardContent>
                      <Typography variant="h6" component="div" gutterBottom>
                        {recipe.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {recipe.description}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Button
                          onClick={() => toggleRecipeExpansion(index)}
                          endIcon={expandedRecipes[index] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          variant="outlined"
                          color="primary"
                        >
                          {expandedRecipes[index] ? 'Hide Details' : 'Show Details'}
                        </Button>
                        <Button
                          onClick={() => printRecipe(recipe)}
                          startIcon={<PrintIcon />}
                          variant="outlined"
                          color="secondary"
                        >
                          Print
                        </Button>
                      </Box>
                      <Collapse in={expandedRecipes[index]}>
                        <Box sx={{ mt: 2 }}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle1" gutterBottom>
                            Ingredients:
                          </Typography>
                          <List dense>
                            {recipe.ingredients.map((ing, i) => (
                              <ListItem key={i}>
                                <ListItemText primary={ing} />
                              </ListItem>
                            ))}
                          </List>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle1" gutterBottom>
                            Instructions:
                          </Typography>
                          <List dense>
                            {recipe.instructions.map((step, i) => (
                              <ListItem key={i}>
                                <ListItemText primary={`${i + 1}. ${step}`} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </Collapse>
                    </CardContent>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {loading && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 9999,
            }}
          >
            <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body1">
                {activeStep === 1 ? 'Identifying ingredients...' : 'Generating recipes...'}
              </Typography>
            </Paper>
          </Box>
        )}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  );
}

export default App;
